/**
    * @description      : 
    * @author           : USER
    * @group            : 
    * @created          : 04/09/2025 - 18:49:51
    * 
    * MODIFICATION LOG
    * - Version         : 1.0.0
    * - Date            : 04/09/2025
    * - Author          : USER
    * - Modification    : 
**/
// src/pages/about/Mission.js
import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { 
  FaBullseye, 
  FaMicrophone, 
  FaPalette, 
  FaRunning,
  FaGlobe,
  FaHeart
} from 'react-icons/fa';

const Mission = () => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.2 });

  const missionAreas = [
    {
      icon: FaMicrophone,
      title: 'Debate & Public Speaking',
      description: 'Training children to express themselves confidently and articulately through structured debate and public speaking programs.'
    },
    {
      icon: FaPalette,
      title: 'Arts & Creativity',
      description: 'Encouraging self-expression and creativity through various artistic mediums and storytelling initiatives.'
    },
    {
      icon: FaRunning,
      title: 'Sports & Physical Development',
      description: 'Promoting physical fitness, teamwork, and discipline through soccer, swimming, and other sporting activities.'
    },
    {
      icon: FaGlobe,
      title: 'Global Platforms',
      description: 'Providing opportunities for children to showcase their talents on international stages and competitions.'
    },
    {
      icon: FaHeart,
      title: 'African Culture',
      description: 'Celebrating and preserving African heritage, values, and cultural identity in all our programs.'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50 pt-24">
      {/* Hero Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="container mx-auto max-w-4xl text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="inline-block px-4 py-2 bg-blue-100 rounded-full border border-blue-200 mb-6">
              <div className="flex items-center space-x-2">
                <FaBullseye className="w-4 h-4 text-blue-600" />
                <span className="text-blue-700 text-sm font-medium">Our Mission</span>
              </div>
            </div>
            
            <h1 className="text-4xl sm:text-5xl font-bold leading-tight mb-8">
              <span className="text-gray-900">Holistic Training for</span>
              <br />
              <span className="text-blue-600">Tomorrow's Leaders</span>
            </h1>
            
            <div className="bg-white rounded-3xl p-8 shadow-lg border border-gray-200 mb-12">
              <p className="text-xl text-gray-700 leading-relaxed font-medium">
                "To provide holistic training, platforms, and opportunities for children to express themselves through debate, public speaking, arts, and sports while celebrating African culture and values."
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Mission Areas Section */}
      <section ref={ref} className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="container mx-auto max-w-6xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">How We Achieve Our Mission</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Through comprehensive programs designed to develop every aspect of a child's potential
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {missionAreas.map((area, index) => {
              const Icon = area.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  animate={inView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="bg-gray-50 rounded-2xl p-6 hover:bg-gray-100 transition-all duration-300 hover:scale-105"
                >
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mb-4">
                    <Icon className="w-6 h-6 text-blue-600" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">{area.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{area.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Impact Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="container mx-auto max-w-6xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-center"
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-8">Our Impact</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
              <div className="bg-white rounded-2xl p-6 shadow-md">
                <div className="text-3xl font-bold text-blue-600 mb-2">100+</div>
                <div className="text-gray-600">Schools Impacted</div>
              </div>
              
              <div className="bg-white rounded-2xl p-6 shadow-md">
                <div className="text-3xl font-bold text-green-600 mb-2">5000+</div>
                <div className="text-gray-600">Students Trained</div>
              </div>
              
              <div className="bg-white rounded-2xl p-6 shadow-md">
                <div className="text-3xl font-bold text-purple-600 mb-2">50+</div>
                <div className="text-gray-600">Championships Hosted</div>
              </div>
              
              <div className="bg-white rounded-2xl p-6 shadow-md">
                <div className="text-3xl font-bold text-orange-600 mb-2">5+</div>
                <div className="text-gray-600">Countries Reached</div>
              </div>
            </div>

            <div className="mt-12 bg-blue-600 rounded-3xl p-8 text-white">
              <h3 className="text-2xl font-bold mb-4">DEBATE. LEAD. CHANGE THE WORLD.</h3>
              <p className="text-blue-100 max-w-2xl mx-auto">
                Every child we train becomes a catalyst for positive change in their community and beyond.
              </p>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Mission;